import React, { FunctionComponent, useEffect, useReducer, useRef } from 'react'
import { useIntl } from '@/utils/intl'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import ReactSignatureCanvas from 'react-signature-canvas'
import { Obj } from '.'
import { useSearchParams } from 'next/navigation'
import { useMobiApi } from '@/services/mobi-api'
import {
  InstanceNewNumberV2,
  SetSignatureReq
} from '@/services/mobi-api/numberv2/types'
import { useToast } from '@/context/ToastContext'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  TextField
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { useLanguage } from '@/context/LanguageContext'
import Loader from '@/components/ui/loader'
import { decodeFromBase64 } from '@/utils/helpers'
import RichText from '@/components/ui/RichText'
import { useCmsApi } from '@/services/cms-api'
import { DigitalConsentData } from '@/services/cms-api/digital-consent/types'
import { IMG_URL } from '@/utils/constants'

interface Props {
  onClick: (objs: Obj) => void
}

interface State {
  instance?: InstanceNewNumberV2
  signature?: string
  pdfData?: string
  termsAgreed?: boolean
  signaturePresent?: boolean
  loading?: boolean
  pdfLoading?: boolean
  loadingIDX?: number
  btnDisable?: boolean
  contractMarkdown?: string
  digitalConsent?: DigitalConsentData
  refCode?: string
  signatureDrawen?: boolean
}

const Step3: FunctionComponent<Props> = ({ onClick }) => {
  const { translate } = useIntl()
  const searchParams = useSearchParams()
  const { numberv2 } = useMobiApi()
  const { getDigitalConsentData } = useCmsApi()
  const { showError } = useToast()
  const { locale } = useLanguage()
  const signatureRef = useRef<ReactSignatureCanvas>(null)

  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      termsAgreed: false,
      signaturePresent: false,
      pdfLoading: true,
      signatureDrawen: false
    }
  )

  const _id = searchParams.get('_id')
  useEffect(() => {
    if (_id !== null && _id !== '') {
      void fetchRequest(_id)
    }
  }, [_id])

  useEffect(() => {
    const storedData = sessionStorage.getItem('mc_refCode')
    setState({ refCode: storedData !== null ? storedData : '' })
  }, [])

  const fetchRequest = async (_id: string): Promise<void> => {
    try {
      setState({ loading: true })
      const res = await numberv2.getInstanceV2(_id, locale)
      if (res !== undefined && res.error === undefined) {
        setState({ instance: res })
        if (res.privacy?.required) {
          const content = await getDigitalConsentData()
          setState({ digitalConsent: content })
        }
      } else {
        showError(
          locale === 'en'
            ? res?.cause?.messageEn ?? res?.error ?? translate('request.failed')
            : res?.cause?.messageMn ?? res?.error ?? translate('request.failed')
        )
      }
    } catch (e) {
      showError(translate('error'))
      console.error(e)
    } finally {
      setState({ loading: false })
    }
  }

  if (state.loading === true) {
    return (
      <div className='flex items-center justify-center w-full h-[50vh]'>
        <Loader />
      </div>
    )
  }

  const handleSignature = async (): Promise<void> => {
    if (signatureRef.current !== null && signatureRef.current !== undefined) {
      const dataURL = signatureRef.current.toDataURL()
      try {
        setState({ btnDisable: true })
        const req: SetSignatureReq = {
          instanceId: state.instance?._id ?? '',
          signature: dataURL,
          consentId:
            state.instance?.privacy.required === true
              ? state.instance?.privacy.sessionId ?? ''
              : '',
          referral: {
            referralCode: state.refCode !== undefined ? state.refCode : '',
            promoCode: 'REFERRAL-PROMO'
          }
        }
        const res = await numberv2.doSign(req)
        if (res !== undefined && res.error === undefined) {
          setState({ btnDisable: false })
          onClick({ instanceId: state.instance?._id })
        } else {
          setState({ btnDisable: false })
          showError(translate('error'))
        }
      } catch (e) {
        showError(translate('error'))
        console.error(e)
      } finally {
        setState({ btnDisable: false })
      }
    }
    sessionStorage.removeItem('mc_refCode')
  }

  const handleBeginSignature = (): void => {
    setState({ signatureDrawen: true })
  }

  const clearSignature = (): void => {
    if (signatureRef.current !== null && signatureRef.current !== undefined) {
      signatureRef.current.clear()
    }
    setState({ signatureDrawen: false })
  }

  return (
    <div className='w-full h-full flex flex-col justify-center items-center p-4'>
      {state.refCode !== undefined && state.refCode !== '' ? (
        <div className='w-full flex bg-surfaceMid rounded-2xl p-2 mb-2'>
          <div className='flex w-full bg-surfaceInverse border border-secondarySoftLow rounded-xl'>
            <TextField
              label={translate('referralPromoCode')}
              className='flex-1 rounded-l-xl text-textHigh'
              value={state.refCode}
              InputProps={{
                readOnly: true
              }}
              sx={{
                '& fieldset': {
                  border: 'none'
                },
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                },
                '& label.Mui-focused': {
                  color: '#262626'
                },
                '& input': {
                  border: 'none',
                  outline: 'none'
                },
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                  {
                    display: 'none'
                  },
                '& input[type="text"]': {
                  MozAppearance: 'textfield'
                }
              }}
            />
            <Button
              className='m-2'
              disabled
              sx={{
                borderRadius: '12px',
                padding: '0 16px',
                textTransform: 'none'
              }}
            >
              <img
                src='/icons/verified.svg'
                height={20}
                width={20}
                alt='info'
              />
            </Button>
          </div>
        </div>
      ) : null}
      <div className='bg-surfaceHigh p-6 md:p-8 rounded-3xl w-full xl:w-1/2 lg:w-3/4 md:w-4/5 flex flex-col gap-4'>
        <span className='text-textHigh font-bold'>
          {translate('agreement.terms')}
        </span>
        {state.instance?.configuratorRes !== undefined &&
        state.instance?.configuratorRes !== undefined ? (
          <div className='bg-surfaceMid border border-secondaryHigh rounded-2xl px-4 py-2 flex flex-col gap-2 w-full'>
            <div className='flex flex-col'>
              <span className='text-textMid text-xs md:text-sm'>
                {translate('number.chosen')}
              </span>
              <span className='text-sm md:text-base font-bold text-textHigh'>
                {state.instance?.newNumber}
              </span>
            </div>
            <hr className='border-[#secondaryMidStroke]' />
            <div className='flex justify-between items-center'>
              <span className='text-xs md:text-sm text-textMid'>
                {translate('number.deposit')}
              </span>
              <span className='text-xs md:text-sm text-textHigh'>
                {state.instance?.configuratorRes.price.depositAmount}₮
              </span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-xs md:text-sm text-textMid'>
                {translate('number.registrationFee')}
              </span>
              <span className='text-xs md:text-sm text-textHigh'>
                {state.instance?.configuratorRes.price.registrationFee}₮
              </span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-xs md:text-sm text-textMid'>
                {translate('number.simFee')}
              </span>
              <span className='text-xs md:text-sm text-textHigh'>
                {state.instance?.configuratorRes.price.simAmount}₮
              </span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-xs md:text-sm text-textMid'>
                {translate('creditControl')}
              </span>
              <span className='text-xs md:text-sm text-textHigh'>
                {state.instance?.configuratorRes.price.creditLimit === 0
                  ? '-'
                  : `${state.instance?.configuratorRes.price.creditLimit}₮`}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-textMid text-xs md:text-sm font-medium'>
                {translate('totalPrice')}:
              </span>
              <span className='text-textHigh text-xs md:text-sm font-bold'>
                {state.instance?.configuratorRes.price.totalAmount}₮
              </span>
            </div>
          </div>
        ) : (
          <></>
        )}
        {state.instance?.rateplan === 'POS97' && (
          <div
            className='w-full box-box-border flex items-center gap-2 border border-[#F4A610] bg-[#FEFCF5] rounded-2xl p-2'
            role='alert'
          >
            <img
              width={32}
              height={30}
              draggable={false}
              src={IMG_URL + '/uploads/icons/warning.svg'}
              alt='warning'
              className='ml-3'
            />
            <span
              className='text-sm overflow-hidden break-words'
              style={{ color: 'rgba(120, 53, 15, 1)' }}
            >
              {translate('package.usageLimitLevel')}
            </span>
          </div>
        )}
        <Accordion
          className='bg-surfaceMid border border-secondaryHigh rounded-2xl'
          sx={{ '&:before': { height: '0px' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore className='text-textHigh' />}
            aria-controls='panel1-content'
            className='text-textHigh text-sm'
          >
            {translate('order.information.userInfo')}
          </AccordionSummary>
          <AccordionDetails>
            <div className='flex flex-col gap-4'>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('user_info.surname')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {state.instance?.danInfo.lastname}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('user_info.name')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {state.instance?.danInfo.firstname}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('user_info.register_no')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {state.instance?.danInfo.regnum}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('email')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {state.instance?.email}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('shop.address')}:
                </span>
                <span className='text-textHigh text-xs text-end font-medium w-1/2'>
                  {state.instance?.danInfo.passportAddress}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('stars.contact')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {state.instance?.contactInfo[0].phoneNumber}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('infoLanguage')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {translate('mongolian')}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('infoType')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {translate('email')}
                </span>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
        {state.instance?.contracts.contract !== undefined &&
        state.instance?.contracts.contract !== null ? (
          state.instance?.contracts.contract.map((item, i) => (
            <Accordion
              key={i}
              className='bg-surfaceMid border border-secondaryHigh rounded-2xl'
              sx={{ '&:before': { height: '0px' } }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore className='text-textHigh' />}
                aria-controls='panel1-content'
                className='text-textHigh text-sm'
              >
                {item.name}
              </AccordionSummary>
              <AccordionDetails className='h-96 overflow-auto'>
                <RichText
                  textSize='text-sm'
                  content={decodeFromBase64(item.content ?? '') ?? ''}
                />
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <></>
        )}
        {state.instance?.privacy?.required === true ? (
          <Accordion
            className='bg-surfaceMid border border-secondaryHigh rounded-2xl'
            sx={{ '&:before': { height: '0px' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore className='text-textHigh' />}
              aria-controls='panel1-content'
              className='text-textHigh text-sm'
            >
              {locale === 'en'
                ? state.digitalConsent?.titleEn ?? ''
                : state.digitalConsent?.titleMn ?? ''}
            </AccordionSummary>
            <AccordionDetails>
              <div className='p-8 pb-36 md:pb-8'>
                <RichText
                  content={
                    locale === 'en'
                      ? state.digitalConsent?.bodyEn ?? ''
                      : state.digitalConsent?.bodyMn ?? ''
                  }
                />
              </div>
            </AccordionDetails>
          </Accordion>
        ) : (
          <></>
        )}
        <div
          className='flex items-center'
          onClick={() => setState({ termsAgreed: state.termsAgreed === false })}
        >
          <Checkbox
            color='error'
            checked={state.termsAgreed}
            inputProps={{ 'aria-label': 'controlled' }}
            className='cursor-pointer text-textHigh'
          />
          <p className='text-xs md:text-sm font-medium cursor-pointer hover:text-primaryMain text-textHigh'>
            {translate('number.terms')}
          </p>
        </div>
        <div className='w-full flex justify-center'>
          <ReactSignatureCanvas
            ref={signatureRef}
            onBegin={handleBeginSignature}
            canvasProps={{
              className: 'signature-canvas bg-surfaceMid',
              style: { borderRadius: '16px', width: '100%', height: '150px' }
            }}
          />
        </div>
        <DarkButton
          type='text'
          fullwidth
          onClick={() => {
            void clearSignature()
          }}
        >
          {translate('clear')}
        </DarkButton>
        <DarkButton
          fullwidth
          disabled={
            !state.termsAgreed || !state.signatureDrawen || state.btnDisable
          }
          onClick={() => {
            void handleSignature()
          }}
        >
          {translate('continue')}
        </DarkButton>
      </div>
    </div>
  )
}

export default Step3
